import { useTranslation } from "react-i18next";
import { AlertTriangle, Ban, FileQuestion, Loader2, Lock, TimerOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Alert, type AlertProps, type DataTableColumn } from "@h2j-data-team/ui";
import type { QlikEvaluation } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import {
  buildQlikSheetRows,
  countNotEvaluated,
  formatMiB,
  formatQlikSeconds,
  formatTimingMs,
} from "@/lib/qlik-evaluation-format";

import { KpiCell, KpiStrip } from "./kpi";
import { ReadOnlyTable } from "./read-only-table";

// -----------------------------------------------------------------------------
// Qlik's own performance evaluation, as shown on the Objects tab.
//
// Six row states, five of which carry no numbers at all. Two of those five are
// DATA, not failures: 'forbidden' (Qlik authorises evaluation per app/space, so
// tenant admin is not enough) and 'no_public_sheets' (Qlik only evaluates
// published sheets). Each gets its own wording — an empty evaluation would be a
// lie in both cases.
// -----------------------------------------------------------------------------

const STATE_ICON: Record<string, LucideIcon> = {
  pending: Loader2,
  running: Loader2,
  forbidden: Lock,
  no_public_sheets: FileQuestion,
  timeout: TimerOff,
  failed: Ban,
};

// Waiting and the two data states are information; a timeout needs a look, a
// failure is the only one that is an error.
const STATE_TONE: Record<string, AlertProps["tone"]> = {
  timeout: "warning",
  failed: "danger",
};

function EvaluationNotice({ status, error }: { status: string; error?: string | null }) {
  const { t } = useTranslation("analyses");
  const Icon = STATE_ICON[status] ?? Ban;
  const spinning = status === "pending" || status === "running";

  return (
    <Alert
      tone={STATE_TONE[status] ?? "info"}
      icon={<Icon className={spinning ? "animate-spin" : undefined} />}
      title={t(`qlikEvaluation.state.${status}.title`)}
    >
      {t(`qlikEvaluation.state.${status}.description`)}
      {error && <p className="mt-1 font-mono text-[11.5px] break-words text-faint">{error}</p>}
    </Alert>
  );
}

export function QlikEvaluationPanel({
  evaluation,
}: {
  evaluation: QlikEvaluation | null | undefined;
}) {
  const { t } = useTranslation("analyses");
  const { formatDateTime } = useFormatDate();

  // The analysis opted out of the Qlik phase: there is nothing to say about it.
  if (!evaluation) return null;

  const payload = evaluation.result;
  const result = payload?.result;
  // 'completed' without a payload should not happen, but a blank block reads as
  // "instant app" — say it plainly instead.
  if (evaluation.status !== "completed" || !result) {
    return (
      <EvaluationNotice
        status={evaluation.status === "completed" ? "failed" : evaluation.status}
        error={evaluation.error}
      />
    );
  }

  const sheets = buildQlikSheetRows(payload);
  const coveredObjects = sheets.reduce((sum, s) => sum + s.coveredObjects, 0);
  const notEvaluated = countNotEvaluated(payload);
  // No sheet and no openApp block are absences, not zeroes: "0 ms" here would
  // read as an instant app.
  const slowestSheet = sheets[0]?.uncachedSeconds;
  const openAppMs = result.openApp?.totalDurationMilliseconds;
  const slowestStep = [...(result.openApp?.steps ?? [])].sort(
    (a, b) => b.durationMilliseconds - a.durationMilliseconds,
  )[0];

  const sheetColumns: DataTableColumn<(typeof sheets)[number]>[] = [
    {
      id: "sheet",
      header: t("qlikEvaluation.sheets.colSheet"),
      rowHeader: true,
      cell: (sheet) => <span className="font-medium">{sheet.title}</span>,
    },
    {
      id: "objects",
      header: t("qlikEvaluation.sheets.colObjects"),
      align: "end",
      variant: "numeric",
      width: 150,
      cell: (sheet) => (
        <span className="text-dim">
          {t("qlikEvaluation.sheets.coverage", {
            covered: sheet.coveredObjects,
            total: sheet.objectCount,
          })}
        </span>
      ),
    },
    {
      id: "cold",
      header: t("qlikEvaluation.sheets.colCold"),
      align: "end",
      variant: "numeric",
      width: 130,
      cell: (sheet) =>
        sheet.uncachedSeconds === undefined ? (
          <span className="text-faint">—</span>
        ) : (
          formatQlikSeconds(sheet.uncachedSeconds)
        ),
    },
    {
      id: "cached",
      header: t("qlikEvaluation.sheets.colCached"),
      align: "end",
      variant: "numeric",
      width: 130,
      cell: (sheet) =>
        sheet.cachedSeconds === undefined ? (
          <span className="text-faint">—</span>
        ) : (
          <span className="text-dim">{formatQlikSeconds(sheet.cachedSeconds)}</span>
        ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-[10.5px] font-semibold tracking-[.08em] text-faint uppercase">
          {t("qlikEvaluation.title")}
        </p>
        <p className="font-mono text-[11.5px] text-faint">
          {[
            payload?.engine?.shortName,
            formatDateTime(evaluation.finishedAt ?? evaluation.createdAt),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      <KpiStrip>
        <KpiCell
          label={t("qlikEvaluation.kpi.slowestSheet")}
          value={slowestSheet === undefined ? "—" : formatQlikSeconds(slowestSheet)}
          valueClassName={slowestSheet === undefined ? "text-faint" : undefined}
          subMeta={t("qlikEvaluation.kpi.slowestSheetSub")}
        />
        <KpiCell
          label={t("qlikEvaluation.kpi.openApp")}
          value={openAppMs === undefined ? "—" : formatTimingMs(openAppMs)}
          valueClassName={openAppMs === undefined ? "text-faint" : undefined}
          subMeta={
            slowestStep
              ? t("qlikEvaluation.kpi.openAppSub", {
                  step: slowestStep.name,
                  value: formatTimingMs(slowestStep.durationMilliseconds),
                })
              : undefined
          }
        />
        <KpiCell
          label={t("qlikEvaluation.kpi.appMemory")}
          value={formatMiB(result.documentSizeMiB)}
          subMeta={t("qlikEvaluation.kpi.appMemorySub", {
            rows: result.rowCount.toLocaleString(),
          })}
        />
        <KpiCell label={t("qlikEvaluation.kpi.sheets")} value={result.sheetCount} />
        <KpiCell
          label={t("qlikEvaluation.kpi.objects")}
          value={result.objectCount}
          subMeta={t("qlikEvaluation.kpi.objectsSub", { covered: coveredObjects })}
        />
      </KpiStrip>

      <ReadOnlyTable
        label={t("qlikEvaluation.sheets.title")}
        columns={sheetColumns}
        rows={sheets}
        getRowId={(sheet) => sheet.sheetId}
        empty={t("qlikEvaluation.sheets.none")}
        toolbar={
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3">
            <p className="text-[10.5px] font-semibold tracking-[.08em] text-faint uppercase">
              {t("qlikEvaluation.sheets.title")}
            </p>
            <p className="text-[11.5px] text-faint">{t("qlikEvaluation.sheets.hint")}</p>
          </div>
        }
      />

      <p className="text-[12px] leading-relaxed text-dim">
        {t("qlikEvaluation.coverageCaveat", {
          covered: coveredObjects,
          total: result.objectCount,
        })}
      </p>
      {notEvaluated > 0 && (
        <Alert tone="warning" icon={<AlertTriangle />}>
          {t("qlikEvaluation.notEvaluated", { count: notEvaluated })}
        </Alert>
      )}
    </div>
  );
}
